import { useRef } from "react";
import {
  motion,
  useMotionValue,
  useSpring,
  useScroll,
  useTransform,
} from "framer-motion";
import { MessageCircle, ArrowDown } from "lucide-react";
import { waLink, HERO_CARD_IMAGE } from "@/data/catalog";

const LINES = ["OGNI BUSTINA", "NASCONDE UNA", "LEGGENDA."];
const EASE = [0.22, 1, 0.36, 1];

const TiltCard = () => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [10, -10]), {
    stiffness: 120,
    damping: 16,
  });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-12, 12]), {
    stiffness: 120,
    damping: 16,
  });

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <div
      style={{ perspective: 1200 }}
      onMouseMove={onMove}
      onMouseLeave={() => {
        mx.set(0);
        my.set(0);
      }}
      className="relative"
      data-testid="hero-tilt-card"
    >
      <motion.div
        style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
        animate={{ y: [0, -12, 0] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        className="relative aspect-[2.5/3.5] w-52 overflow-hidden rounded-md border border-white/10 shadow-[0_40px_120px_-20px_rgba(203,161,83,0.25)] md:w-80"
      >
        <img
          src={HERO_CARD_IMAGE}
          alt="Carta collezionabile Charizard"
          className="h-full w-full object-cover"
        />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-white/10" />
      </motion.div>
      <span className="absolute -left-3 -top-3 h-8 w-8 border-l border-t border-[#cba153]" />
      <span className="absolute -bottom-3 -right-3 h-8 w-8 border-b border-r border-[#cba153]" />
    </div>
  );
};

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const yCard = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, 60]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      ref={ref}
      className="relative flex min-h-[92vh] items-center overflow-hidden px-6 pt-28 md:px-12"
    >
      <div className="pointer-events-none absolute -left-40 top-1/3 h-[480px] w-[480px] rounded-full bg-[#cba153]/10 blur-[140px]" />
      <motion.div
        initial={{ scale: 0.6, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", stiffness: 60, damping: 14 }}
        className="pointer-events-none absolute -right-16 -top-16 hidden lg:block"
      >
        <img
          src="/assets/grandline-logo.jpg"
          alt=""
          className="h-64 w-64 rounded-full object-cover opacity-20 ring-1 ring-[#cba153]/40 blur-[1px]"
        />
      </motion.div>

      <div className="mx-auto grid w-full max-w-7xl items-center gap-16 md:grid-cols-[1.4fr_1fr]">
        <motion.div style={{ y: yText, opacity }}>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.8, ease: EASE }}
            className="mb-8 flex items-center gap-4"
          >
            <img
              src="/assets/grandline-logo.jpg"
              alt="Logo Grand Line Cards"
              data-testid="hero-logo"
              className="h-16 w-16 rounded-full object-cover shadow-[0_0_60px_rgba(203,161,83,0.35)] ring-1 ring-[#cba153]/60"
            />
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#cba153]">
              Grandlinecards — TCG Shop
            </p>
          </motion.div>

          <h1 className="font-display text-4xl font-medium leading-[1.05] tracking-tighter text-[#fafafa] sm:text-6xl lg:text-7xl">
            {LINES.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ delay: 0.25 + i * 0.13, duration: 0.9, ease: EASE }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.75, duration: 0.8, ease: EASE }}
            className="mt-8 max-w-md text-base font-light leading-relaxed text-[#888] md:text-lg"
          >
            Pokémon e One Piece: singole rare, booster box sigillate e tutto il
            necessario per proteggere la tua collezione.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-6"
          >
            <a
              href={waLink("Ciao! Vorrei vedere il catalogo e ordinare.")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-cta"
              className="group flex items-center gap-3 rounded-full bg-[#25D366] px-7 py-4 font-mono text-sm uppercase tracking-[0.15em] text-black transition-transform duration-300 hover:scale-[1.04]"
            >
              <MessageCircle size={18} strokeWidth={2.5} />
              Ordina su WhatsApp
            </a>
            <a
              href="#catalogo"
              onClick={(e) => {
                e.preventDefault();
                if (window.__lenis) window.__lenis.scrollTo("#catalogo", { offset: -40 });
              }}
              data-testid="hero-catalog-link"
              className="group flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-white/60 transition-colors duration-300 hover:text-[#cba153]"
            >
              Vedi il catalogo
              <ArrowDown
                size={14}
                className="transition-transform duration-300 group-hover:translate-y-1"
              />
            </a>
          </motion.div>
        </motion.div>

        <motion.div
          style={{ y: yCard }}
          initial={{ opacity: 0, x: 60 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.5, duration: 1, ease: EASE }}
          className="mt-2 flex justify-center md:mt-0"
        >
          <TiltCard />
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
